import {
  IAutomationActionTemplateSlackInviteToChannel,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  Chip,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch
} from '@material-ui/core';
import { sortBy } from 'lodash';
import { Hash, Lock } from 'lucide-react';
import React, { SetStateAction } from 'react';

import {
  AutocompleteMultiFreeSolo,
  AutocompleteSingle
} from '../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../components/InfoBox';
import { TokenDefinition } from '../../../../../../../components/MarkdownText';
import { SelectCustom } from '../../../../../../../components/SelectCustom';
import { TextFieldWithReplacementTokens } from '../../../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { getUsersByOrgId } from '../../../../../../../services/user';
import {
  getSlackChannelList,
  SlackChannel
} from '../../../../../../Org/pages/Integrations/integrations/slack/service';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_SLACK_INVITE_TO_CHANNEL
} from '../../validation';

export const ChannelTypeSelector = ({
  value,
  onChange
}: {
  value: IAutomationActionTemplateSlackInviteToChannel['channel']['type'];
  onChange: (
    nextValue: IAutomationActionTemplateSlackInviteToChannel['channel']['type']
  ) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <SelectCustom
      fullWidth
      label={t('Channel Type')}
      value={value}
      onChange={(ev) => {
        onChange(
          ev.target
            .value as IAutomationActionTemplateSlackInviteToChannel['channel']['type']
        );
      }}
    >
      <MenuItem value="EXISTING">{t('Use existing channel')}</MenuItem>
      <MenuItem value="NEW">{t('Create a new channel')}</MenuItem>
    </SelectCustom>
  );
};

export const ExistingChannelSelector = ({
  orgId,
  value,
  onChange,
  label
}: {
  orgId: string;
  value: SlackChannel;
  onChange: (nextChannel: SlackChannel) => void;
  label?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <AutocompleteSingle
      label={label ?? t('Channel')}
      fullWidth
      value={value}
      onChange={(nextValue) => {
        if (nextValue) {
          onChange(nextValue);
        }
      }}
      disabledClearable={true}
      options={() =>
        getSlackChannelList(orgId).then((cs) =>
          sortBy(cs, (c) => c.name || 'ZZZZZZZZ')
        )
      }
      getOptionLabel={(o) => o.name}
      renderOption={(o) => (
        <FlexContainer>
          {o.isPrivate && <Lock size={16} />}
          {!o.isPrivate && <Hash size={16} />}
          <div>{o.name}</div>
        </FlexContainer>
      )}
      InputProps={{
        startAdornment: <InputAdornment position="start">#</InputAdornment>
      }}
    />
  );
};

export const ActionSettingsSlackInviteToChannel = ({
  value,
  onChange,
  expTpl,
  validation,
  aTpl,
  setValidation,
  tokens
}: {
  value: IAutomationActionTemplateSlackInviteToChannel;
  onChange: (nextValue: IAutomationActionTemplateSlackInviteToChannel) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_SLACK_INVITE_TO_CHANNEL,
    'pplToAdd'
  );
  const invalidPpltoAdd = validation?.fields.pplToAdd || null;

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          label={t('Who should be invited')}
          value={value.pplToAdd}
          onChange={(nextPplToAdd) => {
            const nextValue = { ...value, pplToAdd: nextPplToAdd };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          error={!!invalidPpltoAdd}
          helperText={invalidPpltoAdd}
          onBlur={() => validatePplToAdd(value, expTpl, aTpl)}
        />
      </FormRow>
      <FormRow>
        <ChannelTypeSelector
          value={value.channel.type}
          onChange={(nextType) => {
            if (nextType === 'EXISTING') {
              onChange({
                ...value,
                channel: {
                  id: '',
                  name: '',
                  isPrivate: true,
                  type: 'EXISTING'
                }
              });
            }
            if (nextType === 'NEW') {
              onChange({
                ...value,
                channel: {
                  nameSchema: '',
                  type: 'NEW',
                  defaultPplEmails: [],
                  isPrivate: true
                }
              });
            }
          }}
        />
      </FormRow>

      {value.channel.type === 'EXISTING' && (
        <>
          <FormRow>
            <ExistingChannelSelector
              orgId={expTpl.orgId}
              value={{
                id: value.channel.id,
                name: value.channel.name,
                isPrivate: value.channel.isPrivate
              }}
              onChange={(nextChannel) => {
                onChange({
                  ...value,
                  channel: {
                    type: 'EXISTING',
                    ...nextChannel
                  }
                });
              }}
            />
          </FormRow>
          <FormRow>
            <InfoBox color="blue">
              {t(
                `Can't find the channel you are looking for? Make sure to invite the Dado bot to the channel!`
              )}
            </InfoBox>
          </FormRow>
        </>
      )}
      {value.channel.type === 'NEW' && (
        <>
          <FormRow>
            <TextFieldWithReplacementTokens
              label="New Channel Name"
              helperText="Enter a channel name without #"
              fullWidth
              variant="outlined"
              value={value.channel.nameSchema}
              onChange={(ev) => {
                value.channel.type === 'NEW' &&
                  onChange({
                    ...value,
                    channel: {
                      ...value.channel,
                      nameSchema: ev
                    }
                  });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">#</InputAdornment>
                )
              }}
              tokens={tokens}
            />
          </FormRow>
          <FormRow>
            <FormControlLabel
              label={t('Make channel private')}
              control={
                <Switch
                  checked={value.channel.isPrivate}
                  onChange={() => {
                    value.channel.type === 'NEW' &&
                      onChange({
                        ...value,
                        channel: {
                          ...value.channel,
                          isPrivate: !value.channel.isPrivate
                        }
                      });
                  }}
                  color="primary"
                />
              }
            />
          </FormRow>

          <FormRow>
            <AutocompleteMultiFreeSolo
              label={t('People to add on channel creation')}
              fullWidth
              value={value.channel.defaultPplEmails}
              onChange={(nextEmails) => {
                value.channel.type === 'NEW' &&
                  onChange({
                    ...value,
                    channel: {
                      ...value.channel,
                      defaultPplEmails: nextEmails
                    }
                  });
              }}
              options={() =>
                getUsersByOrgId(expTpl.orgId).then((us) =>
                  us.map((u) => u.data.email).filter((t) => t)
                )
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="default"
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </FormRow>
        </>
      )}
    </div>
  );
};

export const ActionSettingsSlackInviteToChannelReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateSlackInviteToChannel;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be invited')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Channel Type')}>
          {value.channel.type === 'NEW' && t('Create a new channel')}
          {value.channel.type === 'EXISTING' && t('Use existing channel')}
        </DataFieldWithLabel>
      </FormRow>

      {value.channel.type === 'NEW' && (
        <>
          <FormRow>
            <DataFieldWithLabel label={t('New Channel Name')}>
              {value.channel.nameSchema} {}
            </DataFieldWithLabel>
          </FormRow>

          <FormRow>
            <DataFieldWithLabel label={t('Channel Visibility')}>
              {value.channel.isPrivate ? t('Private') : t('Public')}
            </DataFieldWithLabel>
          </FormRow>

          <FormRow>
            <DataFieldWithLabel label={t('People to add on channel creation')}>
              {joinWithCommaAndAnd(value.channel.defaultPplEmails, t) || '-'}
            </DataFieldWithLabel>
          </FormRow>
        </>
      )}

      {value.channel.type === 'EXISTING' && (
        <>
          <FormRow>
            <DataFieldWithLabel label={t('Channel')}>
              {value.channel.name}
            </DataFieldWithLabel>
          </FormRow>
        </>
      )}
    </div>
  );
};
